/**
 * `/auth/email-verify` — the page an advisor lands on from the verification link
 * Fin sends when it cannot yet trust the mailbox an email came from.
 *
 * Purely presentational. Every decision — redeeming the ticket, recording the
 * verification, deciding which screen applies — lives in
 * `useAdvisorEmailVerification`; this component renders the state it reports.
 *
 * Deliberately separate from `AutoSignInLink`, which does the same redemption
 * for clients. The audience, the copy and the side effect of clicking all differ,
 * and merging them would put two audiences through one set of branches.
 */

import { AlertCircle, MailCheck, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, Card, CardContent, Spinner } from '@design-system';
import { ADVISOR_EMAIL_TRUST_DAYS } from '@shared';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { useAdvisorEmailVerification } from '@client/hooks/use-advisor-email-verification';

function formatExpiry(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function AdvisorEmailVerify() {
  const { state, expiresAt, emailAddress, confirm, retry } = useAdvisorEmailVerification();

  const expiryLabel = formatExpiry(expiresAt);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-20 md:pt-24">
        <div className="w-full max-w-md">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            <div className="p-1">
              <div className="bg-muted rounded-xl px-6 py-10 text-center">
                {state === 'invalidLink' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="flex size-16 items-center justify-center rounded-full bg-red-50">
                        <AlertCircle
                          className="size-9 text-red-600"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      This link isn&apos;t valid
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      Verification links expire after an hour and can only be used once. Reply to
                      your email thread and we&apos;ll send a fresh one.
                    </p>
                  </>
                ) : state === 'confirm' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                        <ShieldCheck
                          className="text-primary size-9"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      Confirm your email address
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      This confirms the mailbox you emailed us from really belongs to you, so we
                      can share client information over email. Only continue if you asked for this
                      link.
                    </p>
                  </>
                ) : state === 'verified' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="flex size-16 items-center justify-center rounded-full bg-green-50">
                        <MailCheck
                          className="size-9 text-green-600"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      You&apos;re all set
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      {emailAddress ? (
                        <>
                          <span className="font-medium text-gray-900">{emailAddress}</span> is
                          confirmed.{' '}
                        </>
                      ) : (
                        'Your email address is confirmed. '
                      )}
                      Please make your original query again and we&apos;ll pick it up from there.
                    </p>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-sm">
                      {expiryLabel
                        ? `We'll ask you to confirm again after ${expiryLabel}.`
                        : `We'll ask you to confirm again in ${ADVISOR_EMAIL_TRUST_DAYS} days.`}
                    </p>
                  </>
                ) : state === 'recordFailed' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="flex size-16 items-center justify-center rounded-full bg-amber-50">
                        <AlertCircle
                          className="size-9 text-amber-600"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      Almost there
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      We couldn&apos;t confirm your email address just now. Try once more — if it
                      still doesn&apos;t work, reply to your email thread for a fresh link.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                        <Spinner className="text-primary size-8" />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      Confirming your email…
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      This will only take a moment.
                    </p>
                  </>
                )}
              </div>
            </div>

            {state === 'confirm' && (
              <CardContent className="p-6">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={confirm}
                >
                  Confirm my email
                </Button>
              </CardContent>
            )}

            {state === 'recordFailed' && (
              <CardContent className="p-6">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={retry}
                >
                  Try again
                </Button>
              </CardContent>
            )}

            {state === 'verified' && (
              <CardContent className="p-6">
                <Button
                  asChild
                  size="lg"
                  className="w-full"
                >
                  {/* External, so a plain anchor rather than a router Link. Opens in a new
                      tab so the confirmation stays visible behind it. */}
                  <a
                    href="https://mail.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Go to Gmail
                  </a>
                </Button>
              </CardContent>
            )}

            {state === 'invalidLink' && (
              <CardContent className="p-6">
                <Button
                  asChild
                  size="lg"
                  className="w-full"
                >
                  <Link to="/">Go to sign in</Link>
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
